import { useEffect, useState } from "react";
import { Loader2, Download, RefreshCw, Plus, ExternalLink, Copy, Check, Inbox } from "lucide-react";
import api, { formatDetail, fmtINR } from "@/lib/api";

const EMPTY = {
    amount: "", client_reference_id: "", method: "", payby_info: "",
    customer: { first_name: "", last_name: "", email: "", mobile: "", address: "", city: "", state: "", country: "IN", postal_code: "" },
    custom_field1: "",
};

/**
 * Partner payin page — "Collect money". Vendor identity is deliberately
 * hidden (user said "partners shouldn't know who the vendor is"). The
 * create flow + status log stays product-branded as "Payouts Portal".
 */
export default function PanelPayin() {
    const [tab, setTab] = useState("new");
    const [orders, setOrders] = useState([]);
    const [mids, setMids] = useState([]);
    const [form, setForm] = useState(EMPTY);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [result, setResult] = useState(null);
    const [copied, setCopied] = useState("");

    const refresh = async () => {
        try {
            const [o, m] = await Promise.all([
                api.get("/partner/payin/orders?limit=50"),
                api.get("/partner/payin/mids"),
            ]);
            setOrders(o.data.items || []);
            setMids(m.data.items || []);
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
    };
    useEffect(() => { refresh(); }, []);

    const createOrder = async () => {
        setBusy(true); setErr(""); setResult(null);
        try {
            const payload = {
                amount: parseFloat(form.amount),
                client_reference_id: form.client_reference_id || `ord_${Date.now()}`,
                method: form.method,
                payby_info: form.payby_info,
                customer: form.customer,
                custom_field1: form.custom_field1,
            };
            const r = await api.post("/partner/payin/orders", payload);
            setResult(r.data.order);
            setForm(EMPTY); refresh();
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    const verify = async (orderId) => {
        try { await api.post(`/partner/payin/orders/${orderId}/verify`); refresh(); }
        catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
    };

    const copy = async (text, key) => {
        try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(""), 1500); } catch {}
    };

    const canCreate = mids.length > 0;

    return (
        <div data-testid="panel-payin" className="p-6 space-y-5">
            <div>
                <h1 className="text-xl font-bold">Collect Payments</h1>
                <p className="text-xs text-slate-500 mt-1">Create a payment link for your customer. We'll notify you when it clears.</p>
            </div>

            {err && <div data-testid="payin-err" className="bg-rose-50 border border-rose-200 rounded p-2 text-xs text-rose-700">{err}</div>}

            {!canCreate && (
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900">
                    Payin isn't enabled on your account yet. Ask your ops contact to assign a payin MID.
                </div>
            )}

            <div className="border-b border-slate-200 flex gap-6">
                {[["new", "New payment"], ["history", `History (${orders.length})`]].map(([k, l]) => (
                    <button key={k} data-testid={`payin-tab-${k}`} onClick={() => setTab(k)}
                            className={`py-2 text-sm border-b-2 -mb-px ${tab === k ? "border-indigo-600 text-indigo-700 font-semibold" : "border-transparent text-slate-500"}`}>{l}</button>
                ))}
            </div>

            {tab === "new" && (
                <div className="bg-white border border-slate-200 rounded-xl p-5 max-w-3xl space-y-4">
                    {result && (
                        <div data-testid="payin-result" className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
                            <div className="text-sm font-semibold text-emerald-900">Order created — status {result.status}</div>
                            <div className="mt-1 text-xs font-mono text-slate-700">{result.order_id}</div>
                            {result.payment_link && (
                                <div className="mt-2 flex items-center gap-2">
                                    <a href={result.payment_link} target="_blank" rel="noreferrer" data-testid="payin-link"
                                            className="text-xs text-indigo-600 hover:underline flex items-center gap-1 break-all">
                                        <ExternalLink className="w-3 h-3" /> Open payment link
                                    </a>
                                    <button onClick={() => copy(result.payment_link, "pl")}
                                            className="p-1 text-slate-500 hover:text-indigo-600">
                                        {copied === "pl" ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                                    </button>
                                </div>
                            )}
                            {result.response_message && <div className="mt-1 text-[11px] text-slate-500">{result.response_message}</div>}
                        </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Amount (INR) *"><input type="number" step="0.01" value={form.amount}
                                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                                className="input w-full text-sm" data-testid="payin-amount" /></Field>
                        <Field label="Your reference ID"><input value={form.client_reference_id}
                                onChange={(e) => setForm({ ...form, client_reference_id: e.target.value })}
                                placeholder="auto-generated if blank" className="input w-full text-sm font-mono"
                                data-testid="payin-ref" /></Field>
                        <Field label="Payment method">
                            <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}
                                        className="input w-full text-sm" data-testid="payin-method">
                                <option value="">Let customer pick</option>
                                <option value="UPI">UPI</option>
                                <option value="CARD">Card</option>
                                <option value="QR">QR</option>
                                <option value="P2P">P2P Transfer</option>
                            </select>
                        </Field>
                        {form.method === "UPI" && (
                            <Field label="UPI VPA (optional)"><input value={form.payby_info}
                                    onChange={(e) => setForm({ ...form, payby_info: e.target.value })}
                                    placeholder="customer@okaxis" className="input w-full text-sm font-mono" /></Field>
                        )}
                    </div>

                    <div className="pt-2 text-xs font-semibold text-slate-700 uppercase tracking-wider">Customer</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {[["first_name", "First name *"], ["last_name", "Last name *"], ["email", "Email *"], ["mobile", "Mobile *"],
                          ["address", "Address"], ["city", "City"], ["state", "State"], ["postal_code", "Postal code"]].map(([k, l]) => (
                            <Field key={k} label={l}>
                                <input value={form.customer[k]}
                                        onChange={(e) => setForm({ ...form, customer: { ...form.customer, [k]: e.target.value }})}
                                        className="input w-full text-sm" data-testid={`payin-cust-${k}`} />
                            </Field>
                        ))}
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                        <button data-testid="payin-create" onClick={createOrder}
                                disabled={busy || !canCreate || !form.amount || !form.customer.first_name || !form.customer.email || !form.customer.mobile}
                                className="btn-primary px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
                            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                            <Plus className="w-4 h-4" /> Create payment link
                        </button>
                    </div>
                </div>
            )}

            {tab === "history" && (
                <div className="bg-white border border-slate-200 rounded-xl">
                    <div className="p-3 border-b border-slate-100 flex items-center justify-between">
                        <div className="text-xs text-slate-500">Click a row to re-poll vendor for latest status.</div>
                        <button onClick={refresh} className="text-xs text-slate-500 hover:text-indigo-600 flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Refresh</button>
                    </div>
                    {orders.length === 0 ? (
                        <div className="p-10 text-center text-xs text-slate-500 flex flex-col items-center gap-2">
                            <Inbox className="w-10 h-10 text-slate-300" />
                            No payment orders yet.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead className="bg-slate-50">
                                    <tr>
                                        <th className="px-3 py-2 text-left">Order</th>
                                        <th className="px-3 py-2 text-left">Customer</th>
                                        <th className="px-3 py-2 text-right">Amount</th>
                                        <th className="px-3 py-2 text-left">Status</th>
                                        <th className="px-3 py-2 text-left">When</th>
                                        <th className="px-3 py-2"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orders.map((o) => (
                                        <tr key={o.order_id} data-testid={`payin-row-${o.order_id}`} className="border-t border-slate-100">
                                            <td className="px-3 py-2 font-mono">{o.order_id}
                                                <div className="text-[10px] text-slate-500">ref {o.client_reference_id}</div>
                                            </td>
                                            <td className="px-3 py-2">{o.customer_name}
                                                <div className="text-[10px] text-slate-500">{o.customer_email}</div>
                                            </td>
                                            <td className="px-3 py-2 text-right font-mono">{fmtINR(o.amount)}</td>
                                            <td className="px-3 py-2">
                                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${o.status === "CAPTURED" ? "bg-emerald-100 text-emerald-800" : o.status === "FAILED" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}>{o.status}</span>
                                            </td>
                                            <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{new Date(o.created_at).toLocaleString()}</td>
                                            <td className="px-3 py-2 text-right">
                                                <button onClick={() => verify(o.order_id)} data-testid={`payin-verify-${o.order_id}`}
                                                        className="text-indigo-600 hover:underline text-[10px]">Verify</button>
                                                {o.payment_link && (
                                                    <a href={o.payment_link} target="_blank" rel="noreferrer"
                                                            className="ml-2 text-indigo-600 hover:underline text-[10px]">Link</a>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function Field({ label, children }) {
    return <div>
        <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">{label}</label>
        <div className="mt-1">{children}</div>
    </div>;
}
